'use client';

import { c as _c } from "react/compiler-runtime";
import { useI18n } from '~/features/i18n';
import { useShouldDisplayPrices, VATKindDisplay } from '~/features/portals/money';
import { useMemo, useRef } from 'react';
import { useHoverState } from '~/modules/browser/DOM';
import { useDeviceType } from '~/components/primitives/hooks/useDeviceType';
import Link from 'next/link';
import { usePortalCtx } from '~/features/global/portal';
import { usePathname } from 'next/navigation';
import { I18N_CONF } from '@funkey/locales/config';
import Image from 'next/image';
import { cx } from 'class-variance-authority';
import Title from '~/components/utils/Title';
import { ActivityCardParticipantsDescription } from './controlUtils';
import { Unit_RenderEUR } from '~/modules/units/components';
import { captureScrollForModalRestoration } from '~/modules/browser/scroll';
import { HISTORY, HREF } from '~/modules/browser/history';
import Icon from '~/components/primitives/Icon';
import useWishlistState from '~/features/wishlist/useWishlistState';
import Tooltip from '~/components/primitives/Tooltip';
import { usePostHog } from 'posthog-js/react';
import { buildActivityHref, type ActivityCardProps } from './index';

/** The PLP card as it looks in production, kept verbatim as the control group of
 * the PLP finetuning experiment. Do not evolve it: when the experiment settles,
 * either this file or `FinetuningCard.tsx` is deleted whole. */
/** Sponsored badge with lightning bolt icon */
const SponsoredBadge: React.FC = () => {
  const $ = _c(4);
  const i18n = useI18n();
  let t0;
  if ($[0] !== i18n) {
    t0 = i18n._("feature.activities.sponsoredBadge", undefined, {
      message: "Sponsored"
    });
    $[0] = i18n;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = <span className="shrink-0">{t0}</span>;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};
export const ActivityCard_Control: React.FC<ActivityCardProps> = props => {
  const i18n = useI18n();
  const portal = usePortalCtx();
  const shouldDisplayPrices = useShouldDisplayPrices();
  const pathname = usePathname();

  // Only show the route icons that match the selected filter (selecting "partner
  // comes to me" hides the "I go to the partner" car, and vice versa). No filter → all.
  const routeFilter = props.routeFilter ?? [];
  const showGoToIcon = props.canGoTo && (routeFilter.length === 0 || routeFilter.includes('FIXED'));
  const showComesToIcon = props.comesToUs && (routeFilter.length === 0 || routeFilter.includes('CUSTOM'));
  // Pill appears only when a city search produced route info (canGoTo/comesToUs).
  const showDistancePill = showGoToIcon || showComesToIcon;

  // The partner's fixed location is further than the searched radius: the activity
  // is only in the results because the partner also comes to you. Drop the km figure
  // from the car pill (it's "out of the question" to travel there) but keep the icon —
  // and explain it in the tooltip.
  const goToIsTooFar = showGoToIcon && props.fixedDistanceKm != null && props.searchRadiusKm != null && props.fixedDistanceKm > props.searchRadiusKm;
  const cardRef = useRef<HTMLElement>(null!);
  const IMG_ASPECT_RATIO = {
    w: 365,
    h: 285
  };
  const [, hoverProps] = useHoverState();
  const wishlist = useWishlistState();
  const posthog = usePostHog();
  const isInWishlist = wishlist.isInWishlist(Number(props.id));
  const {
    isDesktop
  } = useDeviceType();
  const shouldEnableHover = isDesktop;
  const shouldOpenInNewTab = props.openIn === 'new-tab';
  const shouldOpenInModal = props.openIn === 'modal';
  const hoverInteractionProps = shouldEnableHover ? hoverProps : {
    onMouseOver() {},
    onMouseLeave() {}
  };
  const activityHref = useMemo(() => buildActivityHref({
    portal,
    slug: props.slug,
    id: props.id,
    isPrimary: props.isPrimary,
    boostedAdID: props.boostedAdID,
    locale: i18n.locale,
    selectedParticipants: props.selectedParticipants
  }), [portal.portal, portal.baseURLPath, props.slug, props.id, props.isPrimary, props.boostedAdID, i18n.locale, props.selectedParticipants]);

  // Build the final href — append modal=1 for modal mode, or returnUrl for new tab
  const finalHref = useMemo(() => {
    if (shouldOpenInModal) {
      const separator = activityHref.includes('?') ? '&' : '?';
      return `${activityHref}${separator}modal=1`;
    }
    return activityHref;
  }, [activityHref, shouldOpenInModal]);

  // Build href with returnUrl at click time (window.location is mutable, so not in useMemo)
  function getHrefWithReturnUrl() {
    const currentUrl = `${window.location.pathname}${window.location.search}`;
    if (!currentUrl) return finalHref;
    const separator_0 = finalHref.includes('?') ? '&' : '?';
    return `${finalHref}${separator_0}returnUrl=${encodeURIComponent(currentUrl)}`;
  }
  const Wrapper = useMemo<React.FC<typeof hoverProps & {
    className: string;
    children: React.ReactNode;
    ref: React.RefObject<HTMLElement>;
    onClick?(): void;
  }>>(() => wrapperProps => props.disableHref ? <article {...wrapperProps} /> : <article className="contents">
                    <Link {...wrapperProps} ref={wrapperProps.ref as React.RefObject<HTMLAnchorElement>} href={finalHref} scroll={shouldOpenInModal ? false : undefined} target={shouldOpenInNewTab ? '_blank' : undefined} rel={shouldOpenInNewTab ? 'noopener noreferrer' : undefined} onClick={e => {
      // For new tab: open manually with returnUrl, prevent default Link
      if (shouldOpenInNewTab) {
        e.preventDefault();
        window.open(getHrefWithReturnUrl(), '_blank', 'noopener,noreferrer');
      }
      // For non-modal, non-newtab: force hard navigation to avoid
      // the @modal intercept route triggering
      else if (!shouldOpenInModal) {
        e.preventDefault();
        // Set backToOverview before navigating
        const activityPath = I18N_CONF.getPageSlugForLocale('activities', i18n.locale);
        const isOnPDP = pathname.includes(`/${activityPath}/`);
        if (!isOnPDP) {
          HISTORY.specialPages.setValue(prev => ({
            ...prev,
            'PDP.backToOverview': {
              ...HREF.parseURL(window.location),
              portal: portal.portal
            }
          }));
        }
        wrapperProps.onClick?.();
        window.location.href = finalHref;
        return;
      }
      captureScrollForModalRestoration(pathname);

      // Only set backToOverview if we're NOT already on a PDP page
      const activityPath_0 = I18N_CONF.getPageSlugForLocale('activities', i18n.locale);
      const isOnPDP_0 = pathname.includes(`/${activityPath_0}/`);
      if (!isOnPDP_0) {
        const backToOverviewData = {
          ...HREF.parseURL(window.location),
          portal: portal.portal
        };
        HISTORY.specialPages.setValue(prev_0 => ({
          ...prev_0,
          'PDP.backToOverview': backToOverviewData
        }));
      }
      wrapperProps.onClick?.();
    }}>
                        {wrapperProps.children}
                    </Link>
                </article>, [props.disableHref, shouldOpenInNewTab, finalHref, shouldOpenInModal, pathname]);
  return <Wrapper {...hoverInteractionProps} onClick={props.onClick} ref={cardRef} data-testid="activity-card" data-sponsored={!!props.boostedAdID || undefined} className={cx('@container shadow-xs @min-[250px]:p-3 group relative flex h-full flex-col overflow-hidden rounded-3xl bg-white p-2 transition',
  // shouldEnableHover && 'hover:shadow-md',
  // Sponsored ad styling - subtle amber gradient ring (debug only)
  props.debugShowSponsoredStyling && props.boostedAdID && 'ring-2 ring-amber-400/50 ring-offset-1', props.className)}>
            <header className={cx('relative w-full shrink-0 overflow-hidden rounded-2xl bg-blue-100', props.inactive && 'opacity-60 grayscale')} style={{
      aspectRatio: `${IMG_ASPECT_RATIO.w}/${IMG_ASPECT_RATIO.h}`
    }}>
                {props.adminScore ? <div data-testid="activity-card-admin-score" className="absolute bottom-2 right-2 z-10 flex flex-col gap-0.5 rounded-lg bg-blue-900/85 px-2 py-1 font-mono text-[10px] leading-tight text-white backdrop-blur-sm">
                        <span>rank {props.adminScore.rankScore.toFixed(3)}</span>
                        <span>sim {props.adminScore.similarity.toFixed(3)}</span>
                        <span>pop {props.adminScore.popularityBoost.toFixed(3)}</span>
                    </div> : null}
                <Image src={props.mediaPath} alt={`${props.name} - Header`} className={cx('h-full w-full object-cover transition-[filter]', shouldEnableHover && 'group-hover:grayscale-[100%]')} width={IMG_ASPECT_RATIO.w} height={IMG_ASPECT_RATIO.h} />

                {/* Blue-tinted overlay on hover for description readability */}
                <div className={cx('absolute inset-0 bg-blue-800/0 transition-colors', shouldEnableHover && 'group-hover:bg-blue-900/80')} />

                {/* Distance / route buttons (admin-only verification aid). Top-left, on the
                    heart's line, same styling as the wishlist button, revealed on hover. Two
                    separate items: the car ("I go to the partner") always shows its km; the
                    pin ("partner comes to me") shows no km. */}
                {showDistancePill &&
      // Side by side by default; wrap to stacked only when the card is too
      // narrow to fit both pills on one line. `right-12` bounds the width so
      // flex-wrap can break, and leaves room for the wishlist heart.
      <span className="absolute left-3 right-12 top-3 z-1 flex flex-row flex-wrap items-start gap-1">
                        {showGoToIcon && <Tooltip as="span" content={goToIsTooFar ? i18n._('feature.activities.distance.beyondRadiusComesToYou', {
          km: Math.round(props.fixedDistanceKm ?? 0)
        }, {
          message: 'Comes to you, or fixed location at {km} km'
        }) : i18n._('activity.filter.location.option.fixed', undefined, {
          message: 'I go to the partner'
        })} position="top" enabled={isDesktop}>
                                <span className="inline-flex h-8 items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-black/40 px-2.5 text-xs font-semibold leading-none text-white backdrop-blur-sm">
                                    <Icon name="car-retro" strokeWidth={1.5} className="size-4.5!" />
                                    {/* Drop the km when the partner is beyond the searched
                                        radius — it's only listed because it comes to you. */}
                                    {!goToIsTooFar && props.fixedDistanceKm != null && i18n._('feature.activities.distance.km', {
              km: Math.round(props.fixedDistanceKm)
            }, {
              message: '{km} km'
            })}
                                </span>
                            </Tooltip>}
                        {showComesToIcon && <Tooltip as="span" content={i18n._('activity.filter.location.option.custom', undefined, {
          message: 'Location of choice'
        })} position="top" enabled={isDesktop}>
                                <span className="inline-flex h-8 items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-black/40 px-2.5 text-xs font-semibold leading-none text-white backdrop-blur-sm">
                                    <Icon name="house-pin" strokeWidth={1.5} className="size-4!" />
                                    {!isDesktop && i18n._('activity.filter.location.option.custom', undefined, {
              message: 'Location of choice'
            })}
                                </span>
                            </Tooltip>}
                    </span>}

                <span className="absolute right-3 top-3 z-1 flex items-center gap-1.5">
                    {props.hideWishlist ? null : <Tooltip as="span" delay={500} content={isInWishlist ? i18n._('wishlist.remove', undefined, {
          message: 'Remove from wishlist'
        }) : i18n._('wishlist.add', undefined, {
          message: 'Add to wishlist'
        })}>
                            <button type="button" data-testid="activity-card-wishlist" aria-label={isInWishlist ? i18n._('wishlist.remove', undefined, {
            message: 'Remove from wishlist'
          }) : i18n._('wishlist.add', undefined, {
            message: 'Add to wishlist'
          })} className={cx('hit-area-4 md:hit-area-2 rounded-full p-2 backdrop-blur-sm transition', 'hover:scale-105', isInWishlist ? 'border-red-500 bg-white text-red-500 opacity-100' : 'bg-white/20 text-white md:opacity-0 md:group-hover:opacity-100')} onClick={event => {
            event.preventDefault();
            event.stopPropagation();
            const willAdd = !isInWishlist;
            wishlist.toggle({
              activityId: Number(props.id),
              name: props.name,
              slug: props.slug,
              imagePath: props.mediaPath ?? null,
              variantId: null,
              languageId: null,
              addedLanguage: i18n.language
            });
            posthog.capture(willAdd ? 'wishlist.item.added' : 'wishlist.item.removed', {
              activity_id: Number(props.id),
              activity_name: props.name
            });
          }}>
                                <Icon name="heart" size="xs" fill={isInWishlist ? 'currentColor' : 'none'} />
                            </button>
                        </Tooltip>}
                </span>

                {/* Bottom gradient with title — always visible */}
                <div className="absolute bottom-0 left-0 flex w-full flex-col justify-end px-4 pb-2 pt-5 transition-all" style={{
        height: '100%',
        background: shouldEnableHover ? 'linear-gradient(180deg, rgba(0, 0, 0, 0) 40%, rgba(0, 0, 0, 0.2) 85%, rgba(0, 0, 0, 0.5) 100%)' : 'linear-gradient(180deg, rgba(0, 0, 0, 0) 40%, rgba(0, 0, 0, 0.5) 85%, rgba(0, 0, 0, 0.9) 100%)'
      }}>
                    <Title as="h2" size="lg" className="text text-left [text-shadow:2px_2px_4px_rgba(0,0,0,0.3)]" color="light">
                        {props.name}
                    </Title>

                    {/* Description — appears on hover, inside the image */}
                    {props.shortDescription && <div className={cx('mt-2 max-h-0 overflow-hidden opacity-0 transition-all', shouldEnableHover && 'group-hover:max-h-32 group-hover:opacity-100')}>
                            <p className="@min-[300px]:line-clamp-4 mb-1.5 line-clamp-3 text-sm leading-snug text-white/90">
                                {props.shortDescription}
                            </p>
                        </div>}
                </div>
            </header>

            <main className="flex h-full flex-col">
                {shouldDisplayPrices && <div className="@min-[250px]:mt-3 @min-[250px]:p-3 mt-2 flex h-full flex-col justify-between gap-6 p-2">
                        <div className="text-blue-500">
                            <p className="inline-flex w-full gap-1 font-medium">
                                <span className="leading-none! @min-[250px]:text-lg @min-[350px]:text-2xl text-base font-bold text-primary" data-testid="activity-card-price">
                                    {props.price === 0 ? i18n._('price_on_demand') : <Unit_RenderEUR unit="EUR" value={props.price} />}
                                </span>
                                {props.price !== 0 && <>
                                        <span className="@min-[250px]:text-base @min-[350px]:text-xl text-sm">
                                            {' '}
                                            /{' '}
                                        </span>
                                        <span className="@min-[250px]:text-base @min-[350px]:text-xl text-sm lowercase">
                                            {' '}
                                            <span className="@min-[200px]:hidden">
                                                {i18n._('pp', undefined, {
                    message: 'pp'
                  })}
                                            </span>
                                            <span className="@min-[200px]:inline hidden">
                                                {i18n._('person')}
                                            </span>{' '}
                                        </span>{' '}
                                        <span data-testid="activity-card-vat-label" className={cx('ml-auto inline-flex items-center justify-center rounded-3xl bg-blue-50 px-2 py-1 text-xs leading-none opacity-0 transition', shouldEnableHover && 'group-hover:opacity-100')}>
                                            <span
                // Hacky fix because our font isn't vertically aligned very well
                className="h-3">
                                                <VATKindDisplay hideParentheses />
                                            </span>
                                        </span>
                                    </>}
                            </p>
                            <p className="@min-[280px]:text-sm inline-flex w-full items-center justify-between gap-2 text-xs">
                                <span className="truncate">
                                    <ActivityCardParticipantsDescription numParticipants={props.minParticipants} />
                                </span>
                                <span className="inline-flex shrink-0 items-center gap-2">
                                    {props.boostedAdID && <SponsoredBadge />}
                                </span>
                            </p>
                        </div>
                    </div>}
            </main>

            {props.children}
        </Wrapper>;
};